import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styles from "./NavigationPage.module.css";
import { useNavigate, useParams } from "react-router-dom";

/* =========================================================
   API CONFIG
========================================================= */

const API_BASE_URL = "https://coinyoco.world/brobizee-api";

const API_USER_PROFILE_URL = `${API_BASE_URL}/get_user_profile.php`;

/* ⭐ OTP FETCH API */
const API_PARTNER_OTP_URL = `${API_BASE_URL}/match_partner_otp.php`;

/* =========================================================
   HELPERS
========================================================= */

const safeString = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  return String(value).trim() || fallback;
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const parseLatLong = (value) => {
  const raw = safeString(value, "");
  if (!raw) return null;

  const cleaned = raw.replace(/[()[\]]/g, "").trim();
  const parts = cleaned.split(",");
  if (parts.length < 2) return null;

  const lat = Number(parts[0].trim());
  const lng = Number(parts[1].trim());

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return { latitude: lat, longitude: lng };
};

const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/* =========================================================
   PLATFORM DETECTION HELPERS
========================================================= */

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "desktop";

  const ua = (
    navigator.userAgent ||
    navigator.vendor ||
    window.opera ||
    ""
  ).toLowerCase();

  const isAndroid = /android/i.test(ua);
  const isIOS =
    /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/i.test(
    ua
  );
  const isMobile =
    /android|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(
      ua
    );

  if (isTablet) return "tablet";
  if (isAndroid) return "android";
  if (isIOS) return "ios";
  if (isMobile) return "mobile";
  return "desktop";
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function NavigationPage({
  onBack,
  onEnterOtp,
  onTurningNavigation,
  onYourLocation,
}) {
  const navigate = useNavigate();
  const params = useParams();

  const userId = params?.userId || params?.userIdA || "";
  const uuid = params?.uuid || params?.deviceUuid || "";

  /* =========================================================
     STATES
  ========================================================= */

  const [senderData, setSenderData] = useState({
    name: "",
    number: "",
    address: "",
    latLong: "",
    profile: "",
    money: 0,
    chatbox: "",
    request: "",
  });

  const [userData, setUserData] = useState(null);
  const [partnerLocation, setPartnerLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState(
    "Locating your position..."
  );

  /* ⭐ OTP POPUP STATE */
  const [otpPopupOpen, setOtpPopupOpen] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpTimer, setOtpTimer] = useState(30);
  const [otpResendReady, setOtpResendReady] = useState(false);
  const [dbOtp, setDbOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  /* ⭐ NAVIGATION STATE */
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [platform, setPlatform] = useState("desktop");

  /* ⭐ OTP REFS — 4 inputs */
  const otpRef0 = useRef(null);
  const otpRef1 = useRef(null);
  const otpRef2 = useRef(null);
  const otpRef3 = useRef(null);
  const otpRefs = [otpRef0, otpRef1, otpRef2, otpRef3];

  /* =========================================================
     DETECT DEVICE PLATFORM
  ========================================================= */

  useEffect(() => {
    const detected = detectPlatform();
    setPlatform(detected);
    setIsMobileDevice(
      detected === "android" ||
        detected === "ios" ||
        detected === "tablet" ||
        detected === "mobile"
    );
  }, []);

  /* =========================================================
     LOAD SENDER DATA
  ========================================================= */

  useEffect(() => {
    try {
      const name = localStorage.getItem("selected_sender_name") || "";
      const number = localStorage.getItem("selected_sender_number") || "";
      const address = localStorage.getItem("selected_sender_address") || "";
      const latLong =
        localStorage.getItem("selected_sender_lat_long") || "";
      const profile = localStorage.getItem("selected_sender_profile") || "";
      const money = safeNumber(
        localStorage.getItem("selected_sender_money"),
        0
      );
      const chatbox =
        localStorage.getItem("selected_sender_chatbox") || "";
      const request =
        localStorage.getItem("selected_sender_request") || "";

      if (!name && !number) {
        const raw = localStorage.getItem("selected_partner");
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            setSenderData({
              name: safeString(parsed.sender_name, ""),
              number: safeString(parsed.sender_number, ""),
              address: safeString(parsed.sender_address, ""),
              latLong: safeString(parsed.sender_lat_long, ""),
              profile: safeString(parsed.sender_profile_images, ""),
              money: safeNumber(parsed.sender_money, 0),
              chatbox: safeString(parsed.sender_chatbox, ""),
              request: safeString(parsed.sender_request, ""),
            });
            return;
          } catch (error) {
            console.warn("selected_partner parse error:", error);
          }
        }
      }

      setSenderData({
        name,
        number,
        address,
        latLong,
        profile,
        money,
        chatbox,
        request,
      });
    } catch (error) {
      console.warn("Load sender data error:", error);
    }
  }, []);

  /* =========================================================
     FETCH USER DETAILS
  ========================================================= */

  useEffect(() => {
    const fetchUserDetails = async () => {
      const senderId = localStorage.getItem("selected_sender_id") || "";
      const senderUuid =
        localStorage.getItem("selected_sender_uuid") || "";

      if (!senderId || !senderUuid) return;

      try {
        const url =
          `${API_USER_PROFILE_URL}` +
          `?id=${encodeURIComponent(senderId)}` +
          `&device_uuid=${encodeURIComponent(senderUuid)}`;

        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          cache: "no-store",
        });

        if (!response.ok) return;

        const result = await response.json();
        if (result?.success && result?.data) {
          setUserData(result.data);
        }
      } catch (error) {
        console.warn("Fetch user error:", error);
      }
    };

    fetchUserDetails();
  }, []);

  /* =========================================================
     WATCH PARTNER LIVE LOCATION
  ========================================================= */

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus("Location unavailable");
      return;
    }

    let watchId = null;

    try {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          setPartnerLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setLocationStatus("Live location active");
        },
        (error) => {
          console.warn("Location error:", error);
          setLocationStatus("Location permission required");
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 5000,
        }
      );
    } catch (error) {
      console.warn("Geolocation exception:", error);
      setLocationStatus("Unable to access location");
    }

    return () => {
      if (watchId !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, []);

  /* =========================================================
     ⭐ OTP TIMER
  ========================================================= */

  useEffect(() => {
    if (!otpPopupOpen) return;

    setOtpTimer(30);
    setOtpResendReady(false);

    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setOtpResendReady(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [otpPopupOpen]);

  /* =========================================================
     MERGED USER INFO
  ========================================================= */

  const mergedUser = useMemo(() => {
    return {
      name: safeString(senderData.name || userData?.name, "Customer"),
      number: safeString(
        senderData.number || userData?.mobile_number,
        ""
      ),
      address: safeString(
        senderData.address || userData?.location_live,
        "Address not available"
      ),
      latLong: safeString(
        senderData.latLong || userData?.location_live,
        ""
      ),
      profile: safeString(senderData.profile, ""),
      money: safeNumber(senderData.money, 0),
      chatbox: safeString(senderData.chatbox, ""),
      request: safeString(senderData.request, ""),
    };
  }, [senderData, userData]);

  /* =========================================================
     COORDINATES
  ========================================================= */

  const senderCoords = useMemo(
    () => parseLatLong(mergedUser.latLong),
    [mergedUser.latLong]
  );

  /* =========================================================
     DISTANCE
  ========================================================= */

  const distanceKm = useMemo(() => {
    if (!partnerLocation || !senderCoords) return null;

    return calculateDistanceKm(
      partnerLocation.latitude,
      partnerLocation.longitude,
      senderCoords.latitude,
      senderCoords.longitude
    );
  }, [partnerLocation, senderCoords]);

  const distanceText = Number.isFinite(distanceKm)
    ? distanceKm < 1
      ? `${Math.round(distanceKm * 1000)} m`
      : `${distanceKm.toFixed(2)} km`
    : "—";

  /* =========================================================
     ETA
  ========================================================= */

  const etaText = Number.isFinite(distanceKm)
    ? (() => {
        const mins = Math.round((distanceKm / 25) * 60);
        if (mins < 1) return "< 1 min";
        if (mins < 60) return `${mins} min`;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${h}h ${m}m`;
      })()
    : "—";

  /* =========================================================
     MAP (embedded preview only)
  ========================================================= */

  const mapSrc = useMemo(() => {
    if (partnerLocation && senderCoords) {
      return (
        "https://maps.google.com/maps?" +
        `saddr=${partnerLocation.latitude},${partnerLocation.longitude}` +
        `&daddr=${senderCoords.latitude},${senderCoords.longitude}` +
        "&z=14&output=embed"
      );
    }

    if (senderCoords) {
      return (
        "https://maps.google.com/maps?" +
        `q=${senderCoords.latitude},${senderCoords.longitude}` +
        "&z=15&output=embed"
      );
    }

    return "https://maps.google.com/maps?q=India&z=5&output=embed";
  }, [partnerLocation, senderCoords]);

  /* =========================================================
     HANDLERS
  ========================================================= */

  const handleBack = useCallback(() => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    navigate(-1);
  }, [onBack, navigate]);

  /* ⭐ Fallback: Google Maps website (desktop) */
  const openGoogleMapsWeb = useCallback(() => {
    if (!senderCoords) {
      alert("Customer location not available.");
      return;
    }

    const destination = `${senderCoords.latitude},${senderCoords.longitude}`;
    const hasOrigin =
      partnerLocation &&
      Number.isFinite(partnerLocation.latitude) &&
      Number.isFinite(partnerLocation.longitude);

    const url =
      "https://www.google.com/maps/dir/?" +
      "api=1" +
      (hasOrigin
        ? `&origin=${partnerLocation.latitude},${partnerLocation.longitude}`
        : "") +
      `&destination=${encodeURIComponent(destination)}` +
      "&travelmode=driving";

    window.open(url, "_blank", "noopener,noreferrer");
  }, [senderCoords, partnerLocation]);

  /* =========================================================
     ⭐ START NAVIGATION
     • Android → google.navigation:q=LAT,LNG&mode=d
     • iOS     → comgooglemaps://?saddr=..&daddr=..
     • Other   → Google Maps website
  ========================================================= */

  const handleTurningNavigation = useCallback(() => {
    if (!senderCoords) {
      alert("Customer location not available.");
      return;
    }

    /* Parent override wins (if provided) */
    if (typeof onTurningNavigation === "function") {
      onTurningNavigation();
      return;
    }

    const lat = senderCoords.latitude;
    const lng = senderCoords.longitude;

    const hasOrigin =
      partnerLocation &&
      Number.isFinite(partnerLocation.latitude) &&
      Number.isFinite(partnerLocation.longitude);

    const oLat = hasOrigin ? partnerLocation.latitude : "";
    const oLng = hasOrigin ? partnerLocation.longitude : "";

    const currentPlatform = detectPlatform();
    const ua = (navigator.userAgent || "").toLowerCase();

    const isAndroid =
      currentPlatform === "android" || /android/i.test(ua);
    const isIOS =
      currentPlatform === "ios" || /iphone|ipad|ipod/i.test(ua);

    /* ---------------- ANDROID → Native Google Maps app ---------------- */
    if (isAndroid) {
      const nativeUrl = `google.navigation:q=${lat},${lng}&mode=d`;

      const webUrl =
        "https://www.google.com/maps/dir/?" +
        "api=1" +
        (hasOrigin ? `&origin=${oLat},${oLng}` : "") +
        `&destination=${lat},${lng}` +
        "&travelmode=driving";

      try {
        window.location.href = nativeUrl;
      } catch (e) {
        console.warn("Native nav failed, opening web:", e);
        window.open(webUrl, "_blank", "noopener,noreferrer");
        return;
      }

      /* Fallback if Google Maps app not installed */
      setTimeout(() => {
        // If page is still visible after 1.2s, app didn't open → use web
        if (!document.hidden) {
          window.open(webUrl, "_blank", "noopener,noreferrer");
        }
      }, 1200);

      return;
    }

    /* ---------------- iOS → Native Google Maps app ---------------- */
    if (isIOS) {
      const nativeUrl = hasOrigin
        ? `comgooglemaps://?saddr=${oLat},${oLng}&daddr=${lat},${lng}&directionsmode=driving`
        : `comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`;

      const webUrl = hasOrigin
        ? `https://www.google.com/maps/dir/?api=1&origin=${oLat},${oLng}&destination=${lat},${lng}&travelmode=driving`
        : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;

      try {
        window.location.href = nativeUrl;
      } catch (e) {
        console.warn("iOS native nav failed, opening web:", e);
        window.open(webUrl, "_blank", "noopener,noreferrer");
        return;
      }

      setTimeout(() => {
        if (!document.hidden) {
          window.open(webUrl, "_blank", "noopener,noreferrer");
        }
      }, 1200);

      return;
    }

    /* ---------------- Desktop / other → Web ---------------- */
    openGoogleMapsWeb();
  }, [
    senderCoords,
    partnerLocation,
    onTurningNavigation,
    openGoogleMapsWeb,
  ]);

  const handleYourLocation = useCallback(() => {
    if (typeof onYourLocation === "function") {
      onYourLocation();
      return;
    }

    const el = document.querySelector("[data-my-location-card]");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [onYourLocation]);

  const handleCallUser = useCallback(() => {
    const phone = mergedUser.number;
    if (!phone) {
      alert("Customer phone not available.");
      return;
    }
    window.location.href = `tel:${phone}`;
  }, [mergedUser.number]);

  /* =========================================================
     ⭐ OTP POPUP HANDLERS
  ========================================================= */

  const handleOpenOtpPopup = useCallback(async () => {
    setOtpValues(["", "", "", ""]);
    setOtpPopupOpen(true);
    setOtpResendReady(false);
    setOtpTimer(30);
    setDbOtp("");
    setOtpError("");
    setOtpLoading(true);

    setTimeout(() => {
      otpRef0.current?.focus();
    }, 250);

    try {
      const partnerSecondaryId =
        userId || localStorage.getItem("selected_sender_id") || "";

      const partnerSecondaryUuid =
        uuid ||
        localStorage.getItem("selected_sender_uuid") ||
        localStorage.getItem("selected_partner_uuid") ||
        "";

      if (!partnerSecondaryId || !partnerSecondaryUuid) {
        setOtpError("Partner ID or UUID missing.");
        setOtpLoading(false);
        return;
      }

      const url =
        `${API_PARTNER_OTP_URL}` +
        `?secondary_id=${encodeURIComponent(partnerSecondaryId)}` +
        `&secondary_uuid=${encodeURIComponent(partnerSecondaryUuid)}`;

      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      const result = await response.json();

      if (result?.success && result?.data?.otp_code) {
        setDbOtp(String(result.data.otp_code));
      } else {
        setOtpError(result?.message || "OTP not available");
      }
    } catch (err) {
      console.warn("Fetch OTP error:", err);
      setOtpError("Failed to load OTP");
    } finally {
      setOtpLoading(false);
    }
  }, [userId, uuid]);

  const handleCloseOtpPopup = useCallback(() => {
    setOtpPopupOpen(false);
    setOtpValues(["", "", "", ""]);
    setOtpSubmitting(false);
    setDbOtp("");
    setOtpError("");
  }, []);

  const handleOtpChange = useCallback((index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);

    setOtpValues((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });

    if (digit && index < 3) {
      otpRefs[index + 1]?.current?.focus();
    }
  }, []);

  const handleOtpKeyDown = useCallback(
    (index, e) => {
      if (e.key === "Backspace") {
        if (otpValues[index]) {
          setOtpValues((prev) => {
            const next = [...prev];
            next[index] = "";
            return next;
          });
        } else if (index > 0) {
          otpRefs[index - 1]?.current?.focus();
          setOtpValues((prev) => {
            const next = [...prev];
            next[index - 1] = "";
            return next;
          });
        }
      }

      if (e.key === "ArrowLeft" && index > 0) {
        otpRefs[index - 1]?.current?.focus();
      }

      if (e.key === "ArrowRight" && index < 3) {
        otpRefs[index + 1]?.current?.focus();
      }
    },
    [otpValues]
  );

  const handleOtpPaste = useCallback((e) => {
    e.preventDefault();

    const pasted = (e.clipboardData.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, 4);

    if (!pasted) return;

    const next = ["", "", "", ""];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setOtpValues(next);

    const lastIndex = Math.min(pasted.length, 3);
    otpRefs[lastIndex]?.current?.focus();
  }, []);

  const handleAutoFillOtp = useCallback(() => {
    if (!dbOtp) return;

    const digits = dbOtp.replace(/\D/g, "").slice(0, 4).split("");
    const next = ["", "", "", ""];

    digits.forEach((d, i) => {
      next[i] = d;
    });

    setOtpValues(next);

    const lastIndex = Math.min(digits.length, 3);
    otpRefs[lastIndex]?.current?.focus();
  }, [dbOtp]);

  const handleVerifyOtp = useCallback(async () => {
    const code = otpValues.join("");

    if (code.length !== 4) {
      alert("Please enter 4-digit OTP.");
      return;
    }

    setOtpSubmitting(true);

    try {
      if (dbOtp) {
        const cleanDbOtp = dbOtp.replace(/\D/g, "").slice(0, 4);
        if (code !== cleanDbOtp) {
          alert("Invalid OTP. Please enter correct code.");
          setOtpSubmitting(false);
          return;
        }
      }

      if (typeof onEnterOtp === "function") {
        await onEnterOtp(code);
      }

      setOtpPopupOpen(false);

      if (userId && uuid) {
        navigate(`/${userId}/${uuid}/payment`);
      } else {
        navigate("/payment");
      }
    } catch (err) {
      alert(err?.message || "OTP verification failed.");
    } finally {
      setOtpSubmitting(false);
    }
  }, [otpValues, onEnterOtp, userId, uuid, navigate, dbOtp]);

  const handleResendOtp = useCallback(() => {
    if (!otpResendReady) return;

    setOtpValues(["", "", "", ""]);
    setOtpResendReady(false);
    setOtpTimer(30);

    setTimeout(() => {
      otpRef0.current?.focus();
    }, 100);

    console.log("Resend OTP requested");
  }, [otpResendReady]);

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className={styles.screenWrapper}>
      <div className={styles.deviceFrame}>
        <div className={styles.topGlow} />

        <div className={styles.scrollContainer}>
          <main className={styles.mainContent}>
            {/* TOP HEADER */}
            <header className={styles.header}>
              <div className={styles.brandBlock}>
                <span className={styles.brandDot} />
                <span>BroBizee</span>
              </div>

              <div className={styles.headerStatus}>
                <span className={styles.statusPulse} />
              </div>
            </header>

            {/* HERO */}
            <section className={styles.heroSection}>
              <h1 className={styles.userName}>
                Go to...{mergedUser.name}
              </h1>
              <p className={styles.userAddress}>{mergedUser.address}</p>
            </section>

            {/* MAP */}
            <section className={styles.mapCard}>
              <div className={styles.mapContainer}>
                <iframe
                  title="Live Navigation Map"
                  className={styles.mapIframe}
                  src={mapSrc}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />

                <div className={styles.mapShade} />

                <div className={styles.mapLiveBadge}>
                  <span className={styles.liveDot} />
                  <span>LIVE</span>
                </div>

                <div className={styles.redPinMarker}>
                  <div className={styles.pinPulse} />
                  <svg width="34" height="40" viewBox="0 0 34 40" fill="none">
                    <path
                      d="M17 1C8.16 1 1 8.16 1 17C1 28.6 17 39 17 39C17 39 33 28.6 33 17C33 8.16 25.84 1 17 1Z"
                      fill="#E53935"
                      stroke="#FFFFFF"
                      strokeWidth="2"
                    />
                    <circle cx="17" cy="16" r="6" fill="#FFFFFF" />
                  </svg>
                </div>

                <div className={styles.destinationCallout}>
                  <div className={styles.calloutAvatar}>
                    {mergedUser.name?.charAt(0)?.toUpperCase() || "C"}
                  </div>
                  <div className={styles.calloutContent}>
                    <strong>{mergedUser.name}</strong>
                    <span>
                      {mergedUser.address?.slice(0, 42)}
                      {(mergedUser.address?.length || 0) > 42 ? "..." : ""}
                    </span>
                  </div>
                </div>

                <div className={styles.distanceOverlay}>
                  <div className={styles.distanceIcon}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M4 19L20 5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M7 19H4V16"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M17 5H20V8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <strong>
                      {Number.isFinite(distanceKm)
                        ? distanceText
                        : "Locating..."}
                    </strong>
                    {Number.isFinite(distanceKm) && (
                      <span>{etaText} away</span>
                    )}
                  </div>
                </div>

                <div className={styles.blueDotMarker}>
                  <span />
                </div>

                <div className={styles.mapActionsRow}>
                  <button
                    type="button"
                    className={styles.enterOtpBtn}
                    onClick={handleOpenOtpPopup}
                  >
                    <span>ENTER OTP</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 12H19"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M13 6L19 12L13 18"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className={styles.yourLocationBtn}
                    onClick={handleYourLocation}
                  >
                    <span className={styles.targetIcon}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle
                          cx="12"
                          cy="12"
                          r="7"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <circle cx="12" cy="12" r="2" fill="currentColor" />
                        <path
                          d="M12 2V5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M12 19V22"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M2 12H5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M19 12H22"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                    <span>YOUR LOCATION</span>
                  </button>
                </div>
              </div>
            </section>

            {/* LIVE STATUS */}
            <div className={styles.liveLocationBar}>
              <div className={styles.liveLocationLeft}>
                <div className={styles.liveLocationIcon}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 21s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <circle
                      cx="12"
                      cy="9"
                      r="2.5"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div>
                  <strong>{locationStatus}</strong>
                  <span>Your position is updating</span>
                </div>
              </div>
              <span className={styles.liveIndicator}>●</span>
            </div>

            {/* =========================================
                NAVIGATION BUTTON
                • Mobile  → directly opens device Google Maps app
                • Desktop → opens Google Maps website
            ========================================= */}
            <button
              type="button"
              className={styles.navigationBannerBtn}
              onClick={handleTurningNavigation}
            >
              <div className={styles.navigationButtonIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 21s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12Z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <circle
                    cx="12"
                    cy="9"
                    r="2.5"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <span>
                {isMobileDevice
                  ? "Start Navigation"
                  : "Open in Google Maps"}
              </span>
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12H19"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
                <path
                  d="M13 6L19 12L13 18"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* MY LOCATION CARD */}
            <section className={styles.myLocationCard} data-my-location-card>
              <div className={styles.cardTopRow}>
                <div className={styles.myLocationBadge}>
                  <span className={styles.myLocationBadgeDot} />
                  USER DESTINATION
                </div>

                <span className={styles.secureBadge}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 3L19 6V11C19 16 15.8 19.4 12 21C8.2 19.4 5 16 5 11V6L12 3Z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M9 12L11 14L15 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  SECURE
                </span>
              </div>

              <div className={styles.locationCardBody}>
                <div className={styles.customerAvatar}>
                  {mergedUser.name?.charAt(0)?.toUpperCase() || "C"}
                </div>

                <div className={styles.myLocationDetails}>
                  <h3>{mergedUser.name}</h3>

                  {mergedUser.number && (
                    <button
                      type="button"
                      className={styles.phoneButton}
                      onClick={handleCallUser}
                    >
                      <span>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M22 16.9V20C22 21.1 21.1 22 20 22C10.1 22 2 13.9 2 4C2 2.9 2.9 2 4 2H7.1C7.6 2 8 2.3 8.2 2.8L9.7 6.4C9.9 6.9 9.8 7.4 9.4 7.8L7.7 9.5C9 12.1 11.1 14.2 13.7 15.5L15.4 13.8C15.8 13.4 16.3 13.3 16.8 13.5L20.4 15C20.9 15.2 21.2 15.6 21.2 16.1L22 16.9Z"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      {mergedUser.number}
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  className={styles.callButton}
                  onClick={handleCallUser}
                  aria-label="Call customer"
                >
                  <svg width="21" height="21" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M22 16.9V20C22 21.1 21.1 22 20 22C10.1 22 2 13.9 2 4C2 2.9 2.9 2 4 2H7.1C7.6 2 8 2.3 8.2 2.8L9.7 6.4C9.9 6.9 9.8 7.4 9.4 7.8L7.7 9.5C9 12.1 11.1 14.2 13.7 15.5L15.4 13.8C15.8 13.4 16.3 13.3 16.8 13.5L20.4 15C20.9 15.2 21.2 15.6 21.2 16.1L22 16.9Z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <div className={styles.cardFooter}>
                <span>
                  BroBizee
                  <b> • </b>
                  On Time. Online. On Demand.
                </span>
                <span>{userId ? "Connected" : "Ready"}</span>
              </div>
            </section>

            <div className={styles.bottomSpace} />
          </main>
        </div>

        {/* =========================================================
            ⭐ OTP POPUP
        ========================================================= */}

        {otpPopupOpen && (
          <div
            className={styles.otpOverlay}
            onClick={handleCloseOtpPopup}
          >
            <div
              className={styles.otpPopup}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.otpTopStrip} />

              <button
                type="button"
                className={styles.otpCloseBtn}
                onClick={handleCloseOtpPopup}
                aria-label="Close OTP popup"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <line
                    x1="6"
                    y1="6"
                    x2="18"
                    y2="18"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="18"
                    y1="6"
                    x2="6"
                    y2="18"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <div className={styles.otpIconWrap}>
                <span className={styles.otpIconPulse} />
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 3L19 6V11C19 16 15.8 19.4 12 21C8.2 19.4 5 16 5 11V6L12 3Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 12L11 14L15 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div className={styles.otpEyebrow}>
                SECURE VERIFICATION
              </div>

              <h2 className={styles.otpTitle}>Enter OTP</h2>

              <p className={styles.otpSubtitle}>
                Enter the 4-digit code we sent to{" "}
                <strong>{mergedUser.number || "your phone"}</strong> to
                verify and start the service.
              </p>

              {otpLoading && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    padding: "10px 14px",
                    marginBottom: 12,
                    background: "#f0f6ff",
                    border: "1px dashed #4a90e2",
                    borderRadius: 10,
                    color: "#4a90e2",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <span className={styles.otpSpinner} />
                  Loading OTP...
                </div>
              )}

              {!otpLoading && dbOtp && (
                <div
                  style={{
                    display: "none",
                    padding: "12px 16px",
                    marginBottom: 14,
                    background:
                      "linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)",
                    border: "1px solid #a5d6a7",
                    borderRadius: 12,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      color: "#2e7d32",
                      marginBottom: 4,
                      textTransform: "uppercase",
                    }}
                  >
                    Your OTP Code
                  </div>

                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: 800,
                      letterSpacing: 8,
                      color: "#1b5e20",
                      fontFamily: "monospace",
                    }}
                  >
                    {dbOtp}
                  </div>

                  <div
                    style={{
                      fontSize: 11,
                      color: "#558b2f",
                      marginTop: 4,
                    }}
                  >
                    Auto-fill karne ke liye niche click karo
                  </div>

                  <button
                    type="button"
                    onClick={handleAutoFillOtp}
                    style={{
                      marginTop: 8,
                      padding: "6px 14px",
                      background: "#2e7d32",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Auto Fill OTP
                  </button>
                </div>
              )}

              {!otpLoading && otpError && (
                <div
                  style={{
                    padding: "10px 14px",
                    marginBottom: 12,
                    background: "#ffebee",
                    border: "1px solid #ef9a9a",
                    borderRadius: 10,
                    color: "#c62828",
                    fontSize: 13,
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                >
                  {otpError}
                </div>
              )}

              <div
                className={styles.otpInputRow}
                onPaste={handleOtpPaste}
              >
                <input
                  ref={otpRef0}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  className={`${styles.otpDigitBox} ${
                    otpValues[0] ? styles.otpDigitBoxFilled : ""
                  }`}
                  value={otpValues[0]}
                  onChange={(e) => handleOtpChange(0, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(0, e)}
                  onFocus={(e) => e.target.select()}
                  aria-label="OTP digit 1"
                />

                <input
                  ref={otpRef1}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className={`${styles.otpDigitBox} ${
                    otpValues[1] ? styles.otpDigitBoxFilled : ""
                  }`}
                  value={otpValues[1]}
                  onChange={(e) => handleOtpChange(1, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(1, e)}
                  onFocus={(e) => e.target.select()}
                  aria-label="OTP digit 2"
                />

                <input
                  ref={otpRef2}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className={`${styles.otpDigitBox} ${
                    otpValues[2] ? styles.otpDigitBoxFilled : ""
                  }`}
                  value={otpValues[2]}
                  onChange={(e) => handleOtpChange(2, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(2, e)}
                  onFocus={(e) => e.target.select()}
                  aria-label="OTP digit 3"
                />

                <input
                  ref={otpRef3}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className={`${styles.otpDigitBox} ${
                    otpValues[3] ? styles.otpDigitBoxFilled : ""
                  }`}
                  value={otpValues[3]}
                  onChange={(e) => handleOtpChange(3, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(3, e)}
                  onFocus={(e) => e.target.select()}
                  aria-label="OTP digit 4"
                />
              </div>

              <div className={styles.otpTimerRow}>
                {!otpResendReady ? (
                  <span className={styles.otpTimerText}>
                    Resend in <strong>{otpTimer}s</strong>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={styles.otpResendBtn}
                    onClick={handleResendOtp}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M21 12A9 9 0 1 1 12 3"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M21 3V9H15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Resend OTP
                  </button>
                )}
              </div>

              <button
                type="button"
                className={styles.otpSubmitBtn}
                onClick={handleVerifyOtp}
                disabled={
                  otpValues.join("").length !== 4 || otpSubmitting
                }
              >
                {otpSubmitting ? (
                  <>
                    <span className={styles.otpSpinner} />
                    Verifying...
                  </>
                ) : (
                  <>
                    <span>Get Started</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 12H19"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                      />
                      <path
                        d="M13 6L19 12L13 18"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </button>

              <div className={styles.otpFooter}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 3L19 6V11C19 16 15.8 19.4 12 21C8.2 19.4 5 16 5 11V6L12 3Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Your OTP is safe and encrypted</span>
              </div>
            </div>
          </div>
        )}

        {/* ⭐ NAVIGATION APP PICKER HAS BEEN REMOVED
            — Start Navigation now directly opens Google Maps app */}
      </div>
    </div>
  );
}